// FE2b — the Bento canvas. Behind the login gate: signed-out users see the
// sign-in screen; signed-in users get the canvas shell (section tabs + filter
// bar + widget palette + a drag/drop/resize grid, saved to DynamoDB via
// GET/PUT /layouts). The one registered widget for now is the PlayerBio canary;
// FE3+ add the real per-section widgets. Disposable styling ("show Ted + Ben").

// react-grid-layout's stylesheets (positioning + resize handle). Self-contained,
// bundled by Vite — no external requests.
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";
// Our canvas chrome (visible resize grip, drag/placeholder styling) — overrides
// the two RGL stylesheets above, so it must come after them.
import "./canvas.css";

import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { AuthProvider, useAuth } from "./contexts/AuthContext.js";
import SignIn from "./components/auth/SignIn.js";
import CanvasShell from "./components/CanvasShell.js";
import V2App from "./v2/V2App.js";
import { isV2Enabled } from "./v2/flag.js";
import { theme } from "./lib/theme.js";

// One client for the app. Widget data (FE3+) and the layout load share it.
const queryClient = new QueryClient();

/** Chooses what to render based on auth state: loading → gate → canvas. */
function Gate() {
  const { user, loading } = useAuth();
  if (loading)
    return (
      <div
        style={{
          minHeight: "100vh",
          background: theme.page,
          color: theme.inkMuted,
          display: "grid",
          placeItems: "center",
          fontFamily: "system-ui, sans-serif",
        }}
      >
        Loading…
      </div>
    );
  if (!user) return <SignIn />;
  // v2 (the prototype-based frontend) is behind a flag so it can be verified live
  // beside the v1 demo (docs/frontend-v2/). Default stays v1 until the cutover.
  return isV2Enabled() ? <V2App /> : <CanvasShell />;
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <Gate />
      </AuthProvider>
    </QueryClientProvider>
  );
}
